/**
 * Política de autoguardado:
 * - Guarda 2 s después de dejar de escribir.
 * - Con cambios continuos, como máximo una instantánea cada 10 s.
 * - flush() guarda de inmediato (antes de salir del editor o aprobar).
 * No guarda una revisión por tecla. El reloj y los temporizadores se inyectan
 * para poder probarlo de forma determinista.
 */
export interface AutosaveTimers {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export interface AutosaveOptions {
  debounceMs?: number;
  maxWaitMs?: number;
  timers?: AutosaveTimers;
}

export const AUTOSAVE_DEBOUNCE_MS = 2000;
export const AUTOSAVE_MAX_WAIT_MS = 10_000;

const realTimers: AutosaveTimers = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>),
};

export class AutosaveScheduler {
  private readonly debounceMs: number;
  private readonly maxWaitMs: number;
  private readonly timers: AutosaveTimers;
  private handle: unknown = null;
  private firstDirtyAt: number | null = null;
  private lastFireAt = Number.NEGATIVE_INFINITY;

  constructor(private readonly onFire: () => void, opts: AutosaveOptions = {}) {
    this.debounceMs = opts.debounceMs ?? AUTOSAVE_DEBOUNCE_MS;
    this.maxWaitMs = opts.maxWaitMs ?? AUTOSAVE_MAX_WAIT_MS;
    this.timers = opts.timers ?? realTimers;
  }

  get pending(): boolean {
    return this.firstDirtyAt !== null;
  }

  notifyChange(): void {
    const now = this.timers.now();
    if (this.firstDirtyAt === null) this.firstDirtyAt = now;
    const debounceAt = now + this.debounceMs;
    const maxWaitAt = Math.max(this.firstDirtyAt + this.maxWaitMs, this.lastFireAt + this.maxWaitMs);
    const at = Math.min(debounceAt, maxWaitAt);
    this.schedule(Math.max(0, at - now));
  }

  flush(): void {
    if (this.firstDirtyAt === null) return;
    this.fire();
  }

  cancel(): void {
    if (this.handle !== null) this.timers.clearTimeout(this.handle);
    this.handle = null;
    this.firstDirtyAt = null;
  }

  private schedule(ms: number): void {
    if (this.handle !== null) this.timers.clearTimeout(this.handle);
    this.handle = this.timers.setTimeout(() => this.fire(), ms);
  }

  private fire(): void {
    if (this.handle !== null) this.timers.clearTimeout(this.handle);
    this.handle = null;
    this.firstDirtyAt = null;
    this.lastFireAt = this.timers.now();
    this.onFire();
  }
}

// ---------------------------------------------------------------------------
// Estados visibles de guardado. «Guardado» sin calificativo solo tras confirmación.
// ---------------------------------------------------------------------------
export type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; serverTime: string; revisionNumber?: number }
  | { kind: 'pending_offline'; since: string }
  | { kind: 'conflict'; conflictId: string | null }
  | { kind: 'error'; message: string; retryable: boolean };

export function saveStateLabel(state: SaveState, timeZone?: string): string {
  switch (state.kind) {
    case 'idle':
      return 'Sin cambios';
    case 'saving':
      return 'Guardando…';
    case 'saved': {
      const hhmm = new Intl.DateTimeFormat('es', { hour: '2-digit', minute: '2-digit', timeZone }).format(new Date(state.serverTime));
      return `Guardado en nube a las ${hhmm}`;
    }
    case 'pending_offline':
      return 'Pendiente de conexión';
    case 'conflict':
      return 'Conflicto: hay dos versiones';
    case 'error':
      return state.retryable ? `No se pudo guardar; se reintentará (${state.message})` : `No se pudo guardar: ${state.message}`;
  }
}
